export const TOTVS_PROVIDERS = ['guru', 'hotmart'] as const;

export type TotvsProvider = (typeof TOTVS_PROVIDERS)[number];

/**
 * O destino que o de-para resolve para a venda: `enroll` matricula no RM,
 * `ignore` descarta o job com motivo, sem tocar no RM — é o que existe para o
 * produto que a Primum não vende mais e continua chegando por recorrência.
 * Mesmo vocabulário do back (`TOTVS_PRODUCT_LINK_ACTIONS`).
 */
export const TOTVS_PRODUCT_LINK_ACTIONS = ['enroll', 'ignore'] as const;

export type TotvsProductLinkAction =
  (typeof TOTVS_PRODUCT_LINK_ACTIONS)[number];

export interface TotvsProductLink {
  id: string;
  provider: TotvsProvider;
  /**
   * Conta do gateway (`ProviderAccount`) que o de-para escopa. `null` = curinga:
   * casa qualquer conta do gateway (comportamento legado). Um valor específico só
   * casa eventos daquela conta — um evento do mesmo produto/oferta vindo de outra
   * conta é ignorado, mesmo com o marcador [TV].
   */
  providerAccountId: string | null;
  productExternalId: string;
  offerExternalId: string;
  action: TotvsProductLinkAction;
  /** Obrigatório em `ignore`, `null` em `enroll`. */
  ignoreReason: string | null;
  /** Os quatro campos do RM vêm `null` quando `action` é `ignore`. */
  idAreaInteresse: string | null;
  codColigada: number | null;
  codFilial: number | null;
  idPs: string | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  perPage: number;
}

export const TOTVS_SYNC_STATUSES = [
  'pending',
  'in_progress',
  'waiting_product_link',
  'delivered',
  'failed',
  'ignored',
] as const;

export type TotvsSyncStatus = (typeof TOTVS_SYNC_STATUSES)[number];

/**
 * `mcb` = `SaveRecord`/`ZMDSALUNO` único (fluxo original). `worknow` = Plano B
 * (Fórmulas Visuais 7+5), 2 chamadas por job com espera de 90s entre elas.
 */
export type TotvsSyncStrategy = 'mcb' | 'worknow';

/** Só relevante quando `strategy === 'worknow'`; `null` em jobs `mcb`. */
export type TotvsWorknowPhase = 'awaiting_baixa' | null;

export interface TotvsEnrollmentPayload {
  codColigada: number;
  idPs: string;
  idAreaInteresse: string;
  cpf: string;
  nome: string;
  dtNascimento: string;
  email: string;
  telefone: string;
  cep: string;
  endereco: string;
  numero: string;
  complemento: string | null;
  bairro: string;
  cidade: string;
  estado: string;
  pais: string;
}

export interface TotvsSyncAttempt {
  attemptedAt: string;
  request: string | null;
  rawResponse: string | null;
  result: string;
  errorCode: string | null;
  errorMessage: string | null;
}

export interface TotvsSyncTask {
  id: string;
  contractId: string;
  userId: string;
  transactionId: string;
  invoiceId: string;
  provider: string;
  providerAccountId: string | null;
  productExternalId: string;
  offerExternalId: string;
  offerName: string;
  strategy: TotvsSyncStrategy;
  worknowPhase: TotvsWorknowPhase;
  status: TotvsSyncStatus;
  statusReason: string | null;
  payload: TotvsEnrollmentPayload | null;
  failedAttempts: number;
  lastAttemptAt: string | null;
  lastError: string | null;
  errorCode: string | null;
  errorBucket: string | null;
  attempts: TotvsSyncAttempt[];
  inCurrentStatusSince: string;
  createdAt: string;
  updatedAt: string;
}

export interface ListTotvsSyncTasksInput {
  page?: number;
  perPage?: number;
  status?: TotvsSyncStatus;
  productExternalId?: string;
  errorCode?: string;
  userId?: string;
}

export interface ListTotvsProductLinksInput {
  page?: number;
  perPage?: number;
  provider?: TotvsProvider;
  providerAccountId?: string;
  productExternalId?: string;
  offerExternalId?: string;
  action?: TotvsProductLinkAction;
  idAreaInteresse?: string;
  codColigada?: number;
  codFilial?: number;
  idPs?: string;
  enabled?: boolean;
}

/**
 * A chave de `findMatch` — provider + conta resolvida + par produto/oferta.
 * Diferente de `ListTotvsProductLinksInput`, não é igualdade simples: o back
 * aplica a precedência específico-vence-curinga.
 */
export interface TotvsProductLinkMatchInput {
  provider: TotvsProvider;
  providerAccountId: string;
  productExternalId: string;
  offerExternalId: string;
}

export interface ReprocessTotvsSyncTaskInput {
  reason?: string;
}

export interface ProcessTotvsSyncTaskNowInput {
  /**
   * Confirma a matrícula de um job barrado pelo snapshot do `totvs-middleware`
   * (motivo `matriculado_pelo_middleware:*`) — soltar matricula o aluno de novo
   * no RM.
   *
   * Vive aqui, e não no "Marcar como pendente", porque o portão é reavaliado no
   * processamento: aquele caminho só devolve o job à fila e o worker o barraria
   * de novo. O override só é respeitável na mesma execução que chama o RM.
   */
  overrideMiddlewareGate?: boolean;
}

export interface UpdateTotvsSyncTaskPayloadInput {
  payload: TotvsEnrollmentPayload;
  reason?: string;
}

export interface ReprocessBatchTotvsSyncsInput {
  status?: TotvsSyncStatus;
  errorCode?: string;
  /** Prefixo do motivo — libera só um lote de `IGNORED` (ex.: fim do teste). */
  statusReason?: string;
  reason?: string;
}

export interface ReprocessBatchTotvsSyncsOutput {
  /** Quantidade de tasks resetadas para PENDING nesta rodada. */
  reprocessed: number;
  /** `true` se o filtro casou mais que o teto por rodada (resto fica p/ a próxima). */
  capped: boolean;
  /** Jobs pulados por estarem barrados pelo `totvs-middleware` — nunca vão em lote. */
  skippedHandledByMiddleware: number;
}

export interface ProcessTotvsSyncTaskNowOutput {
  outcome: 'delivered' | 'failed' | 'waiting_product_link' | 'skipped';
  task: TotvsSyncTask;
}

/**
 * Os campos do RM são opcionais aqui porque o destino decide quais fazem
 * sentido: em `ignore` eles não vão no payload e a justificativa é obrigatória.
 * Quem recusa a combinação incoerente é a entidade do back — o formulário só
 * evita a viagem.
 */
export interface CreateTotvsProductLinkInput {
  provider: TotvsProvider;
  /** A conta do gateway é obrigatória: um de-para pertence a uma conta. */
  providerAccountId: string;
  productExternalId: string;
  offerExternalId: string;
  action?: TotvsProductLinkAction;
  ignoreReason?: string | null;
  idAreaInteresse?: string | null;
  codColigada?: number | null;
  codFilial?: number | null;
  idPs?: string | null;
  enabled?: boolean;
}

export interface UpdateTotvsProductLinkInput {
  provider?: TotvsProvider;
  providerAccountId?: string;
  productExternalId?: string;
  offerExternalId?: string;
  action?: TotvsProductLinkAction;
  ignoreReason?: string | null;
  idAreaInteresse?: string | null;
  codColigada?: number | null;
  codFilial?: number | null;
  idPs?: string | null;
  enabled?: boolean;
}

// --- Alertas operacionais do TOTVS (Fase 5 do plano de alertas compartilhados) ---

/**
 * Espelha o status genérico de `_shared/alerts` do monolith
 * (`OperationalAlertStatus`) — **maiúsculo**. Confirmado contra o banco (`totvs_operational_alerts.status`) e
 * contra o 400 que `@IsEnum(OperationalAlertStatus)` devolve para qualquer
 * valor fora de `OPEN`/`RESOLVED`/`DISMISSED`.
 */
export const TOTVS_OPERATIONAL_ALERT_STATUSES = [
  'OPEN',
  'RESOLVED',
  'DISMISSED',
] as const;
export type TotvsOperationalAlertStatus =
  (typeof TOTVS_OPERATIONAL_ALERT_STATUSES)[number];

/**
 * ASSUNÇÃO — contrato exato não confirmado por este front: o backend da Fase 5
 * (`TotvsAdminController`, worktree `feature/shared-operational-alerts` do
 * primum-monolith, outro repo) expõe `GET /admin/totvs/operational-alerts` com
 * um `TotvsOperationalAlertOutputDto` com o shape do `OperationalAlert` do
 * shared (os campos abaixo, os mesmos de `SalesOperationalAlert` em
 * `sales.entity.ts`). O que não está confirmado são os
 * *valores* literais de `type`/`entityType` — o vocabulário do TOTVS para esses
 * dois campos, então ficam como `string` aberta em vez de união fechada.
 * Ajustar para união fechada quando o contrato real for confirmado (ver
 * `humanizeSnakeCase` em `presentation/pending-source.ts`, que é o fallback de
 * rótulo enquanto isso).
 */
export interface TotvsOperationalAlert {
  id: string;
  type: string;
  entityType: string;
  entityId: string;
  status: TotvsOperationalAlertStatus;
  inThisStatusSince: string;
  details: Record<string, unknown>;
  resolutionNote: string | null;
  resolvedByPlatformProfileId: string | null;
  resolutionAttachmentFileId: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Filtros server-side — espelham `ListTotvsOperationalAlertsQueryDto` (Fase 5):
 * `page`/`perPage`/`status`/`type`/`entityType`/`entityId`.
 */
export interface ListTotvsOperationalAlertsInput {
  page?: number;
  perPage?: number;
  status?: TotvsOperationalAlertStatus;
  type?: string;
  entityType?: string;
  entityId?: string;
  /** `asc` = a mais antiga primeiro (a ordem da Central de Pendências). Default do back: `desc`. */
  order?: 'asc' | 'desc';
}

/** Uma opção da faceta de tipo: o `type` cru do back e quantos alertas há dele. */
export interface OperationalAlertFacet {
  value: string;
  count: number;
}

/** Corpo de `resolve`/`dismiss` de um alerta operacional — espelha `CloseTotvsOperationalAlertDto`. */
export interface CloseTotvsOperationalAlertInput {
  note: string;
  attachmentFileId?: string;
}

/**
 * `entityType` usado no histórico de auditoria (`GET .../entities/:entityType/:id/events`) —
 * espelha `TotvsEntityType.OPERATIONAL_ALERT` do monolith (`totvs-audit.enums.ts`).
 * Único valor consumido pelo front hoje (a Central de Pendências só audita alertas).
 */
export const TOTVS_OPERATIONAL_ALERT_ENTITY_TYPE = 'totvs_operational_alert';

/**
 * `entityType` da task de sincronização — espelha `TotvsEntityType.SYNC_TASK`
 * do monolith (`totvs-audit.enums.ts`), o valor que `TotvsAlerts.recordSyncTaskFailed`
 * grava no alerta. É por ele que a Central de Pendências sabe que o `entityId`
 * daquele alerta abre em `/totvs/jobs/:taskId`.
 */
export const TOTVS_SYNC_TASK_ENTITY_TYPE = 'totvs_sync_task';

/** Formato HTTP de um `EntityEvent` do TOTVS — espelha `TransactionEvent` do Sales. */
export interface TotvsEntityEvent {
  id: string;
  eventType: string;
  actorPlatformProfileId: string | null;
  actorName: string | null;
  reason: string | null;
  payload: Record<string, unknown>;
  correlationId: string | null;
  createdAt: string;
}

export interface TotvsEntityEventsResponse {
  items: TotvsEntityEvent[];
  total: number;
  page: number;
  perPage: number;
}
